import React, { useEffect, useState, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Button,
  Col,
  Content,
  Grid,
  HelpBlock,
  Icon,
  Message,
  Panel,
  Placeholder,
  Row
} from 'rsuite';
import { Redirect, useParams } from 'react-router-dom';
import { get, pick } from 'lodash/fp';
import { MANAGE_GUEST } from '../../../constants/custom-list/Types';
import { MERGE as GUEST_MERGE } from '../../../constants/permissions/guest';
import { fullAccess } from '../../../helpers/permission';
import {
  fetchGuestForMerge,
  mergeGuest
} from '../../../redux/Guest/sagas/routines';
import {
  getGuestMergeData as getGuest,
  getIsSaving,
  getIsFetched
} from '../../../redux/Guest/selectors/routes/merge';
import { parseInt } from '../../../helpers/utils';
import pagePathToUrl from '../../../helpers/pagePathToUrl';
import formatFullName from '../../../helpers/formatFullName';
import { guestsBasePath, guestsViewPath } from '../../Paths';
import PageHeader from '../../../components/PageHeader';
import PageLayout from '../../../components/guest/PageLayout';
import AccessDenied from '../../../components/AccessDenied';
import AccessControl from '../../../components/AccessControl';
import ConfirmationModal from '../../../components/ConfirmationModal';
import GuestSearch from '../../../containers/GuestSearch';
import CustomBreadcrumb from '../../../components/Breadcrumb';
import GuestDetails from './GuestDetails';

export default () => {
  const dispatch = useDispatch();
  const { id: guestId } = useParams();
  const [sourceGuest, setSourceGuest] = useState(null);
  const [showMergeConfirmation, setShowMergeConfirmation] = useState(false);
  const isMerging = useSelector(getIsSaving);
  const isFetched = useSelector(getIsFetched);
  const destinationGuest = useSelector(getGuest);

  const guestFullName = formatFullName(
    pick(['firstname', 'middlename', 'lastname'], destinationGuest)
  );

  useEffect(() => {
    dispatch(fetchGuestForMerge(guestId));
  }, []);

  const handleSelectGuest = guest => {
    if (get('id', guest) === parseInt(guestId)) {
      Alert.error('Please select a different Guest to merge with');
    } else {
      setSourceGuest(guest);
    }
  };

  const handleShowMergeConfirmation = () => {
    setShowMergeConfirmation(true);
  };

  const handleCancelMerge = () => {
    setSourceGuest(null);
  };

  const handleConfirmMerge = () => {
    dispatch(mergeGuest({ id: guestId, sourceId: sourceGuest.id }));
    setShowMergeConfirmation(false);
  };

  const handleCancelConfirmation = () => {
    setShowMergeConfirmation(false);
  };

  if (isFetched && !destinationGuest) {
    return <Redirect to={guestsBasePath} />;
  }

  return (
    <PageLayout>
      <AccessControl
        allowOnlyFor={fullAccess(GUEST_MERGE)}
        fallback={<AccessDenied />}
      >
        <CustomBreadcrumb
          items={[
            { path: guestsBasePath, label: 'Guests' },
            {
              path: pagePathToUrl(guestsViewPath, {
                id: guestId,
                section: 'primary'
              }),
              label: guestFullName
            },
            { label: 'Merge' }
          ]}
        />
        <PageHeader title="Merge Guests" />
        <Content style={{ marginTop: 0 }}>
          <Panel shaded style={{ marginBottom: 20 }}>
            <Grid fluid>
              <Row>
                {destinationGuest && (
                  <Col xs={5}>
                    {sourceGuest && (
                      <HelpBlock style={{ marginBottom: 5 }}>
                        <Icon
                          icon="check-circle"
                          style={{ color: '#4caf50' }}
                        />{' '}
                        Preserved
                      </HelpBlock>
                    )}
                    <GuestDetails guest={destinationGuest} />
                  </Col>
                )}
                <Col xs={3}>
                  <Icon
                    size="4x"
                    icon="long-arrow-left"
                    style={{ marginTop: 12 }}
                  />
                </Col>
                <Col xs={8}>
                  {sourceGuest ? (
                    <Fragment>
                      <HelpBlock style={{ marginBottom: 5 }}>
                        <Icon
                          icon="close-circle"
                          style={{ color: '#f44336' }}
                        />{' '}
                        Permanently Deleted
                      </HelpBlock>
                      <GuestDetails guest={sourceGuest} />
                    </Fragment>
                  ) : (
                    <Placeholder.Paragraph rows={3} />
                  )}
                </Col>
              </Row>
              {sourceGuest && (
                <Row style={{ marginTop: 20 }}>
                  <Col xs={24}>
                    <Message
                      showIcon
                      type="info"
                      title="Merge Reminder"
                      description={
                        <div>
                          Merging these two guests will permanently delete the
                          guest on the &#34;right side&#34;.
                          <br />
                          Details such as Visits, Notes, Audit Trail, Files and
                          Reservations will be transfered to the &#34;left
                          side&#34; guest.
                        </div>
                      }
                      style={{ marginBottom: 20 }}
                    />
                    <Button
                      disabled={isMerging}
                      style={{ marginRight: 10 }}
                      onClick={handleCancelMerge}
                    >
                      <Icon icon="chevron-left" /> Back
                    </Button>
                    <Button
                      disabled={isMerging}
                      appearance="primary"
                      onClick={handleShowMergeConfirmation}
                    >
                      <Icon icon={isMerging ? 'spinner' : 'compress'} /> Merge
                      Guests
                    </Button>
                  </Col>
                </Row>
              )}
            </Grid>
          </Panel>

          {!sourceGuest && (
            <Fragment>
              <h6 style={{ marginBottom: 10 }}>
                Select the guest to merge with:
              </h6>
              <GuestSearch
                hideCreate
                type={MANAGE_GUEST}
                showBarcodeSearch={false}
                onSelectGuest={handleSelectGuest}
              />
            </Fragment>
          )}

          <ConfirmationModal
            show={showMergeConfirmation}
            title="Merge Confirmation"
            onConfirm={handleConfirmMerge}
            onCancel={handleCancelConfirmation}
            cancelButtonLabel="Cancel"
            confirmButtonLabel="Yes"
          >
            Are you sure you want to merge these Guests?
          </ConfirmationModal>
        </Content>
      </AccessControl>
    </PageLayout>
  );
};
